// @ts-check
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import {
  Alert,
  AlertVariant,
  Bullseye,
  Button,
  ButtonVariant,
  EmptyState,
  EmptyStateBody,
  EmptyStateIcon,
  EmptyStateVariant,
  InputGroup,
  TextInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  ToolbarItemVariant,
  Spinner,
  Stack,
  StackItem,
  OverflowMenuContent,
  OverflowMenuGroup,
  OverflowMenuItem, EmptyStateHeader, EmptyStateFooter, InputGroupItem, Tooltip, PaginationVariant, OverflowMenu
} from '@patternfly/react-core';

import {
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  InnerScrollContainer, ActionsColumn, TableVariant
} from '@patternfly/react-table';
import styles from '@patternfly/react-styles/css/components/Table/table';

import { useTranslation } from 'localization/useTranslation';
import '@patternfly/react-core/dist/styles/base.css';
import T from 'localization/LocalizationKeys';
import Tl from 'localization/LocalizationKeys';

import PropTypes from 'prop-types';
import { isItemInCollection } from 'components/Common/utils/collection';
import Icons from 'components/Common/Icons';
import { search } from 'components/Common/utils/collection';
import classNames from 'classnames';
import './SimpleTable.css';
import useBoolean from 'utils/hooks/useBoolean';
import useSimpleTablePager from 'components/Common/SimpleTable/useSimpleTablePager';
import useDeferredValue from 'utils/hooks/useDeferredValue';
import LocalizedPagination from './LocalizedPagination';

const alignRight = /** @type {const} */ ({ default: 'alignRight' });

/**
 * Internal state values for the table
 * @readonly
 * @enum {string}
 */
const State = {
  LOADING: 'loading',
  EMPTY: 'empty',
  DATA: 'data'
};

/**
 * Values for sort direction
 * @readonly
 * @enum {string}
 */
export const SortDirection = {
  ASC: 'asc',
  DESC: 'desc'
};

/**
 * Values for row selection
 * @readonly
 * @enum {string|undefined}
 */
export const RowSelection = {
  NONE: undefined,
  SINGLE: 'single',
  MULTI: 'multi',
  SINGLE_CHECKBOX: 'single_checkbox',
  MULTI_CHECKBOX: 'multi_checkbox'
};

/**
 * @template {Record<string, any>} DataType
 * @callback ColumnRenderCallback
 * @param {any} data
 * @param {DataType} rowData
 * @param {number} rowIndex
 * @returns {JSX.Element|string}
 */
/**
 * @template {Record<string, any>} DataType
 * @typedef {string|React.ReactNode} ColumnItemTooltip
 */
/**
 * @template {Record<string, any>} DataType
 * @callback ColumnItemTooltipRenderCallback
 * @param {any} data
 * @param {DataType} rowData
 * @param {number} rowIndex
 * @returns {ColumnItemTooltip<DataType>}
 */

/**
 * @template {Record<string, any>} DataType
 * @typedef {string | import('@patternfly/react-table/dist/esm/components/Table/base/types').ThInfoType} ColumnInfo
 */

/**
 * @template {Record<string, any>} DataType
 * @callback ColumnInfoRenderCallback
 * @param {ColumnType<DataType>} columnInfo
 * @return {ColumnInfo<DataType>}
 */

/**
 * @template {Record<string, any>} DataType
 * @template [KeyInDataType = Extract<keyof DataType, string>] - The property of the data
 * @typedef {Object} ColumnType
 * @property {KeyInDataType | '.'} path - Path to the property in the data
 * @property {string} label - Column label
 * @property {ColumnItemTooltip<DataType>|ColumnItemTooltipRenderCallback<DataType>} [itemTooltip] - Show tooltip on a single item cell
 * @property {ColumnInfo<DataType>|ColumnInfoRenderCallback<DataType>} [info] - Tooltip on the column header
 * @property {ColumnRenderCallback<DataType>} [render] - Render function for the cell when doing custom components
 * @property {boolean} [searchItem=false] - True if the data in the column should be searchable
 * @property {boolean} [isSortable=false] - True if the column should be sortable
 */
const ColumnPropType = PropTypes.shape({
  path: PropTypes.string.isRequired,
  label: PropTypes.string,
  render: PropTypes.func,
  searchItem: PropTypes.bool,
  isSortable: PropTypes.bool
});


const ActionPropType = PropTypes.shape({
  title: PropTypes.string,
  variant: PropTypes.oneOf(_.values(ButtonVariant)),
  onClick: PropTypes.func
});

/**
 * @template {Record<string, any>} DataType
 * @template [KeyInDataType = keyof DataType] - The property of the data
 * @typedef {Object} SortByType
 * @property {KeyInDataType} path
 * @property {SortDirection} direction
 */
const SortByPropType = PropTypes.shape({
  path: PropTypes.string,
  direction: PropTypes.oneOf(_.values(SortDirection))
});

/**
 * Determines whether selection is multi or not
 * @param {RowSelection} selection
 * @returns {boolean} True if selection is multi
 */
const isMultiSelection = selection => selection === RowSelection.MULTI || selection === RowSelection.MULTI_CHECKBOX;

/**
 * Determines whether selection is checkbox select or not
 * @param {RowSelection} selection
 * @returns {boolean} True if selection is checkbox select
 */
const isCheckboxSelection = selection => selection === RowSelection.SINGLE_CHECKBOX || selection === RowSelection.MULTI_CHECKBOX;

const PageInfoPropType = PropTypes.shape({
  page: PropTypes.number,
  perPage: PropTypes.number,
  onSetPage: PropTypes.func,
  onPerPageSelect: PropTypes.func,
  itemCount: PropTypes.number
});

/**
 * @callback BooleanDataCallback
 * @param {object} data
 * @returns {boolean} True if action is disabled
 */


/**
 * @template {Record<string, any>} DataType
 * @typedef {Object} ActionType
 * @property {string} [title] Action label/title if no label is provided TODO: Determine if we should go with label or title See GlobalAction
 * @property {function (DataType): JSX.Element} [render] Custom render function, overrides default 'Button' render
 * @property {function (DataType, number): void} [onClick]
 * @property {BooleanDataCallback} [isDisabled]
 * @property {boolean} [isPersistent=false]
 * @property {boolean} [isOverflow=false]
 * @property {ButtonVariant} [variant] Button variant
 */


/**
 * A Global action that is rendered in the toolbar
 * @typedef {Object} GlobalAction
 * @property {string=} label Action label
 * @property {string=} title Action label/title if no label is provided TODO: Determine if we should go with label or title
 * @property {function (object): JSX.Element} [render] Custom render function, overrides default 'Button' render
 * @property {ButtonVariant=} variant Button variant
 * @property {boolean=} isLoading True if action/table is loading or otherwise busy
 * @property {React.MouseEventHandler=} onClick callback when button is pressed
 * @property {boolean=} isDisabled True if action is disabled
 */

const GlobalActionPropType = PropTypes.shape({
  render: PropTypes.func,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  title: PropTypes.string,
  variant: PropTypes.oneOf(_.values(ButtonVariant)),
  onClick: PropTypes.func,
  isDisabled: PropTypes.bool
});

/**
 * @typedef {Object} SimpleTableToolbarActionProps
 * @prop {GlobalAction} action
 */

/**
 * @description Renders a global action in the toolbar
 * @param {SimpleTableToolbarActionProps} props
 * @returns {JSX.Element}
 */
const SimpleTableToolbarAction = ({ action }) => {
  if (action.render) {
    return action.render({ action });
  }

  return (<Button
    isLoading={action.isLoading}
    variant={action.variant}
    onClick={action.onClick}
    isDisabled={action.isDisabled} >
    { action.label || action.title }
  </Button>
  );
};

/**
 *
 * @callback OnSearchCallback
 * @param {string} searchTerm - the search term
 * @returns {void}
 */

/**
 * @typedef {Object} DefaultToolbarItemsProps
 * @prop {import('./LocalizedPagination').PageInfo} [pageInfo]
 * @prop {string} [searchText]
 * @prop {GlobalAction[]} [actions]
 * @prop {OnSearchCallback} [onSearch]
 */


/**
 *
 * @param {DefaultToolbarItemsProps} prop
 * @returns {JSX.Element}
 */
const DefaultToolbarItems = ({ pageInfo, onSearch, searchText, actions }) => {
  return (
    <>
      { onSearch && (
        <ToolbarItem>
          <InputGroup>
            <InputGroupItem isFill >
              <TextInput
                name="textInput1" id="textInput1" type="search" aria-label="search input"
                onChange={(_event, value) => onSearch(value)} value={searchText}/>
            </InputGroupItem>
            <InputGroupItem><Button variant={ButtonVariant.control} aria-label="search button for search input">
              <Icons.Search />
            </Button>
            </InputGroupItem>
          </InputGroup>
        </ToolbarItem>
      )
      }
      {
        _.map(actions, (action, index) => (
          <ToolbarItem key={index}>
            <SimpleTableToolbarAction action={action} />
          </ToolbarItem>
        ))
      }
      { pageInfo && (
        <ToolbarItem variant={ToolbarItemVariant.pagination} align={alignRight}>
          <LocalizedPagination pageInfo={pageInfo} />
        </ToolbarItem>
      )
      }
    </>);
};

/**
 * @template {Record<string, any>} DataType
 * @typedef {Object} AdjustedColumn
 * @extends {ColumnType<DataType>}
 * @property {boolean} [isDraggable]
 * @property {boolean} [isCheckbox]
 */

/**
 * @template {Record<string, any>} DataType
 * @param {ColumnType<DataType>[]} columns
 * @param {RowSelection} rowSelection
 * @param {boolean} isDraggable
 * @returns {AdjustedColumn<DataType>[]} Adjusted columns
 */
const adjustColumnsForSelection = (columns, rowSelection, isDraggable) => {
  const dragColumn = isDraggable ? { isDraggable: true } : null;
  if (isCheckboxSelection(rowSelection)) {
    return _.compact([
      // Have selection: Add either checkbox or an empty placeholder (single select)
      rowSelection === RowSelection.SINGLE_CHECKBOX ? { isCheckbox: true } : { isCheckbox: true, isMulti: true },
      dragColumn,
      ...columns]);
  }
  return _.compact([dragColumn, ...columns]);
};

/**
 * @template DataType
 * @callback onSortCallback
 * @param {ColumnType<DataType>} column
 * @param {SortDirection} direction
 * @returns {void}
 */

/**
 * Render column headings
 * @template DataType
 * @typedef {Object} ColumnsProps
 * @prop {ColumnType<DataType>[]} columns
 * @prop {onSortCallback<DataType>} onSort
 * @prop {SortByType<DataType>} [sortBy]
 * @prop {RowSelection} rowSelection
 * @prop {function (boolean): void} onSelectAll
 * @prop {boolean} isAllSelected
 * @prop {array} actions
 * @prop {boolean} isDraggable
 */

/**
 * @template DataType
 * @param {string} tooltip
 * @returns {import('@patternfly/react-table/dist/esm/components/Table/base/types').ThInfoType}
 */
const createColumnInfo = (tooltip) => {
  return { tooltip };
};

/**
 * @template DataType
 * @param {ColumnType<DataType>} column
 * @return {import('@patternfly/react-table/dist/esm/components/Table/base/types').ThInfoType | undefined}
 */
const getColumnInfo = (column) => {
  if (_.isFunction(column.info)) {
    const infoResult = column.info(column);
    if (_.isString(infoResult)) {
      return createColumnInfo(infoResult);
    }
    return infoResult;
  }

  if (_.isString(column.info)) {
    return createColumnInfo(column.info);
  }

  return column.info;
};

/**
 * @template DataType
 * @param {ColumnType<DataType>} column
 * @param {CellType} cell
 * @param {DataType} rowData
 * @param {number} rowIndex
 * @returns {ColumnItemTooltip<DataType>|undefined}
 */
const getTdContent = (column, cell, rowData, rowIndex) => {
  let tooltipContent = null;
  if (_.isFunction(column.itemTooltip)) {
    tooltipContent = column.itemTooltip(cell.data, rowData, rowIndex);
  } else {
    tooltipContent = column.itemTooltip;
  }

  if (tooltipContent != null) {
    return <Tooltip content={tooltipContent}><label>{ cell.content }</label></Tooltip>;
  }
  return cell.content;
};

/**
 * Creates a column heading
 * @template DataType
 * @param {ColumnsProps<DataType>} props
 * @returns {JSX.Element}
 */
const Columns = ({ columns, onSort, sortBy, rowSelection, onSelectAll, isAllSelected, actions, isDraggable }) => {
  const T = useTranslation();
  const adjustedColumns = adjustColumnsForSelection(columns, rowSelection, isDraggable);
  const indexAdjustOffset = adjustedColumns.length - columns.length;

  const activeSortIndex = useMemo(() => {
    // @ts-ignore-next-line
    let index = _.findIndex(columns, { path: sortBy?.path });
    if (index !== -1) {
      return index + indexAdjustOffset;
    }
    return index;
  }, [columns, sortBy?.path, indexAdjustOffset]);

  const _onSort = useCallback((unusedEvent, index, direction) => {
    onSort(columns[index - indexAdjustOffset], direction);
  }, [columns, onSort, indexAdjustOffset]);

  const standardColums =
    adjustedColumns.map((column, columnIndex) => {
      const key = uuidv4();
      if (column.isCheckbox) {
      // All selection in multi selection, this can be disabled by not providing an onSelectAll
        if (column.isMulti && onSelectAll) {
          return (<Th
            width={column?.width}
            info={getColumnInfo(column)}
            key={key}
            select={{
              onSelect: (unused, isSelecting) => onSelectAll(isSelecting),
              isSelected: isAllSelected
            }}/>);
        }
        return (<Th
          key={columnIndex}
          width={column?.width}
          info={getColumnInfo(column)}
        />);

      }

      if (column.isDraggable) {
        return (<Th key={columnIndex} info={getColumnInfo(column)} />);
      }

      const sortParams = column.isSortable ? {
        sort: {
          sortBy: /** @type {import('@patternfly/react-table/dist/esm/components/Table/TableTypes').ISortBy} */({
            index: activeSortIndex,
            direction: sortBy?.direction
          }),
          onSort: _onSort,
          columnIndex
        }
      } : {};
      return (<Th
        key={key}
        {...sortParams}
        width={column?.width}
        info={getColumnInfo(column)}>
        { T.localize(column?.label) }
      </Th>);
    });

  if (!_.isEmpty(actions)) {
    return <>{ [...standardColums, <Td key={uuidv4()}/>] }</>;
  }
  return <>{ standardColums }</>;
};

/**
 * Handle click on action/global action
 * @template DataType
 * @param {object} data
 * @param {number} [index] - Row index (if row action)
 * @returns {function (ActionType<DataType>): import('@patternfly/react-table').IAction}
 */
const onClickAction = (data, index) => (action) => ({ ..._.omit(action, ['isDisabled', 'isPersistent']), onClick: () => action.onClick(data, index), isDisabled: checkIsDisabled(action, data) });

/**
 * Remap onClick action, on all actions, to receive row data as first argument
 * @template DataType
 * @param {ActionType<DataType>[]} actions List of actions for the row
 * @param {DataType} row Row data
 * @param {number} [rowIndex] Row index if row action
 * @returns {import('@patternfly/react-table').IActions} New list of actions with onClick remapped to receive row data as argument
 */
const mapOnClickAction = (actions, row, rowIndex) => {
  return _.map(actions, onClickAction(row, rowIndex));
};

/**
 * @typedef {object} CellType
 * @prop {string | React.ReactElement<any, string | React.JSXElementConstructor<any>> & React.ReactNode} content
 * @prop {any} data
 */

/**
 * Render cells as plain data or using the columns 'render' property if it exists
 * @template {Record<string, any>} DataType
 * @param {DataType} dataRow
 * @param {number} dataIndex
 * @param {ColumnType<DataType>} column
 * @returns {CellType}
 */
const renderCell = (dataRow, dataIndex, column) => {
  if (column.path === '.') { // send object to render
    const content = column.render ? column.render(dataRow, dataRow, dataIndex) : 'Must have render function if full object is used';
    return {
      data: dataRow,
      content
    };
  }
  const data = _.get(dataRow, column.path);
  return {
    data,
    content: column.render ? column.render(data, dataRow, dataIndex) : data
  };
};

const showMenuOnBodyProps = { direction: 'down', position: 'end', appendTo: (() => document.body) };
const isValidDrop = (event, body) => {
  const ulRect = body.getBoundingClientRect();
  return event.clientX > ulRect.x && event.clientX < ulRect.x + ulRect.width &&
  event.clientY > ulRect.y && event.clientY < ulRect.y + ulRect.height;
};

/**
 * @template DataType
 * @callback OnRowClickCallback
 * @param {DataType} rowData
 * @param {number} rowIndex
 * @param {MouseEvent | React.MouseEvent<Element>} event
 * @returns {void}
 */

/**
 * @template {Record<string, any>} DataType
 * @callback OnSelectedRowsCallback
 * @param {DataType[]} selectedRows
 * @returns {void}
 */

/**
 * Check if action is disabled by calling isDisabled function or using the isDisabled property
 * @param {GlobalAction|ActionType<Record<string, any>>} action
 * @param {object} data
 * @returns
 */
const checkIsDisabled = (action, data) => {
  if (_.isFunction(action?.isDisabled)) {
    return action.isDisabled(data);
  }
  return action?.isDisabled;
};

/**
 *
 */
const OverflowItem = ({ action, data, rowIndex }) => {
  return (
    <Button
      icon={action.icon}
      isDisabled={checkIsDisabled(action, data)}
      key={action.title}
      variant={action.variant}
      onClick={(event) => {
        onClickAction(data, rowIndex)(action).onClick(event, rowIndex, data, null);
      }}
    >
      { action.title }
    </Button>
  );
};

/**
 * @template DataType
 * @typedef {Object} OverflowContentProps
 * @prop {number} rowIndex
 * @prop {ActionType<DataType>[]} items
 * @prop {object} data
 */

/**
 * @template DataType
 * @param {OverflowContentProps<DataType>} props
 * @returns {JSX.Element}
 */
const OverflowContent = ({ rowIndex, items, data }) => {
  if (_.isEmpty(items)) {
    return null;
  }
  const isPersistent = _.some(items, item => item.isPersistent);
  return (
    <OverflowMenu breakpoint="lg">
      <OverflowMenuContent isPersistent={isPersistent}>
        <OverflowMenuGroup groupType="button" isPersistent={isPersistent}>
          { _.map(items, (action, actionIndex) => (
            <OverflowMenuItem
              key={`${ rowIndex }_buttonaction_${actionIndex}`}
              isPersistent={action.isPersistent}>
              <OverflowItem action={action} data={data} rowIndex={rowIndex} />
            </OverflowMenuItem>
          ))
          }
        </OverflowMenuGroup>
      </OverflowMenuContent>
    </OverflowMenu>
  );
};

/**
 * @template DataType
 * @typedef {object} RowsProps
 * @prop {array} data Row data
 * @prop {ColumnType<DataType>[]} columns Column data for all rows
 * @prop {OnRowClickCallback<DataType>} onRowClick Callback when a row is clicked
 * @prop {ActionType<DataType>[]} actions Menu actions for a row
 * @prop {RowSelection} rowSelection
 * @prop {array} selectedRows Current selected rows
 * @prop {OnSelectedRowsCallback<DataType>} onSelectedRows
 * @prop {function (DataType): any} rowIteratee
 * @prop {function} onDragStart
 * @prop {function} onDragEnd
 * @prop {function} onDragCancel
 * @prop {function} onDrop
 * @prop {React.RefObject} bodyRef
 * @prop {boolean} isDraggable
 * @prop {function} setDraggedItemId
 */

/**
 * Render all rows
 * @template DataType
 * @param {RowsProps<DataType>} props
 * @returns {JSX.Element}
 */
const Rows = ({
  data, columns,
  onRowClick,
  actions,
  rowSelection,
  selectedRows,
  onSelectedRows,
  rowIteratee,
  onDragStart,
  onDragEnd,
  onDragCancel,
  onDrop,
  bodyRef,
  isDraggable,
  setDraggedItemId
}) => {
  const rows = useMemo(() =>
    _.map(data, (dataRow, /** @type {number}*/dataIndex) =>
      _.map(columns, column => renderCell(dataRow, dataIndex, column))),
  [data, columns]);

  const T = useTranslation();

  const isRowSelected = useCallback(item => isItemInCollection(selectedRows, rowIteratee)(item),
    [selectedRows, rowIteratee]);

  const _onRowClick = useCallback((rowData, rowIndex, event) => {

    // Creates a new array of selected items
    const createSelected = (row, isSelecting) => {
      const otherSelectRows = selectedRows?.filter(r => r !== row) ?? [];
      if (isMultiSelection(rowSelection)) {
        return isSelecting ? [...otherSelectRows, row] : otherSelectRows;
      }
      return isSelecting ? [row] : otherSelectRows;

    };

    if (rowSelection) {
      const newSelected = createSelected(rowData, !isRowSelected(rowData));
      onSelectedRows?.(newSelected);
    } else {
      onRowClick?.(rowData, rowIndex, event);
    }
  }, [isRowSelected, onRowClick, onSelectedRows, rowSelection, selectedRows]);


  const onDragStartCallback = useCallback((event) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', event.currentTarget.id);
    const draggedItemId = event.currentTarget.id;
    event.currentTarget.classList.add(styles.modifiers.ghostRow);
    event.currentTarget.setAttribute('aria-pressed', 'true');
    setDraggedItemId(draggedItemId);
    onDragStart();
  }, [onDragStart, setDraggedItemId]);

  const onDragEndCallback = useCallback((event) => {
    const target = event.target;
    target.classList.remove(styles.modifiers.ghostRow);
    target.setAttribute('aria-pressed', 'false');
    setDraggedItemId(null);
    onDragEnd();
  }, [onDragEnd, setDraggedItemId]);

  const body = bodyRef.current;
  const onDropCallback = useCallback((event) => {
    if (isValidDrop(event, body)) {
      onDrop();
    } else {
      onDragCancel();
    }
  }, [body, onDragCancel, onDrop]);

  const [overflowItems, menuActions] = useMemo(() => _.partition(actions, action => action.isOverflow === true), [actions]);
  // TODO: Check the performance of the click events as we are probably introducing multiple click events both on row and cell
  return (<>{ rows.map((row, rowIndex) => (
    <Tr
      className="simple-table-row"
      id={isDraggable ? rowIteratee(data[rowIndex]) : undefined}
      isClickable
      draggable={isDraggable}
      onDrop={onDropCallback} onDragEnd={onDragEndCallback} onDragStart={onDragStartCallback}
      key={rowIndex}
      onRowClick={(rowSelection !== RowSelection.NONE) ? event => _onRowClick?.(data[rowIndex], rowIndex, event) : null}
      isRowSelected={!isCheckboxSelection(rowSelection) && isRowSelected(data[rowIndex])}>
      { isCheckboxSelection(rowSelection) && (
        <Td
          key={rowIndex}
          dataLabel={''}
          select={{
            rowIndex,
            onSelect: (event) => _onRowClick?.(data[rowIndex], rowIndex, event),
            isSelected: isRowSelected(data[rowIndex])
          }} />
      ) }
      { isDraggable && <Td draggableRow={{ id: `draggable-row-${rowIndex}` }} /> }
      { row.map((cell, cellIndex) => {
        const column = columns[cellIndex];
        const rowData = data[rowIndex];
        const content = getTdContent(column, cell, rowData, rowIndex);
        return (<Td
          key={`${rowIndex}_${cellIndex}`}
          dataLabel={T.localize(column?.label)}
          onClick={(event) => {
            onRowClick?.(data[rowIndex], rowIndex, event);
          }}
        > { content } </Td>);
      })
      }
      { !_.isEmpty(actions) && (
        <Td
          isActionCell
          key={`${ rowIndex }_action`}>
          <OverflowContent rowIndex={rowIndex} data={data[rowIndex]} items={overflowItems} />
          { !_.isEmpty(menuActions) && (
            <ActionsColumn
              popperProps={showMenuOnBodyProps}
              rowData={row}
              items={mapOnClickAction(menuActions, data[rowIndex], rowIndex)}
            />
          ) }
        </Td>
      ) }
    </Tr>
  )) }</>);
};

/**
 * @typedef {Object} CreateDefaultActionsProps
 * @prop {function} [onDelete]
 * @prop {function} [onEdit]
 * @prop {boolean} [isOverflow]
 * @prop {boolean} [useIcons]
 */

/**
 * Create default actions for list
 * @param {CreateDefaultActionsProps} props
 * @returns {array}
 */
export const createDefaultActions = ({ onDelete, onEdit, isOverflow, useIcons }) => (_.compact(
  [
    onDelete && (
      (useIcons && {
        icon: <Icons.Delete />,
        onClick: onDelete,
        isOverflow,
        variant: ButtonVariant.plain
      }) || {
        title: T.common.delete,
        variant: ButtonVariant.primary,
        onClick: onDelete,
        isOverflow
      }),

    onEdit && {
      title: T.common.edit,
      onClick: onEdit,
      isOverflow
    }
  ]
));

const move = (itemOrder, ulNode) => {
  const nodes = Array.from(ulNode.children);
  if (nodes.map(node => node.id).every((id, i) => id === itemOrder[i])) {
    return;
  }
  while (ulNode.firstChild) {
    ulNode.removeChild(ulNode.lastChild);
  }
  itemOrder.forEach(id => {
    const child = nodes.find(n => n.id === id);
    ulNode.appendChild(child);
  });
};


const moveItem = (arr, i1, toIndex) => {
  const fromIndex = arr.indexOf(i1);
  if (fromIndex === toIndex) {
    return arr;
  }
  const temp = arr.splice(fromIndex, 1);
  arr.splice(toIndex, 0, temp[0]);
  return arr;
};

/**
 * @template DataType
 * @callback DatafilterCallback
 * @param {DataType[]} data
 * @param {ColumnType<DataType>[]} columns
 * @param {string} searchText
 * @param {SortByType<DataType>} sortBy
 * @returns {DataType[]} - Filtered data
 */


/**
 * @template DataType
 * @callback OrderChangedCallback
 * @param {DataType[]} data
 */

/**
 * @template DataType
 * @callback PageChangedCallback
 * @param {DataType[]} pageData
 * @param {number} page - Current page
 * @param {number} perPage - Number of items per page
 * @returns {void}
 */

/**
 * @template DataType
 * @callback SimpleTableOnSortCallback
 * @param {SortByType<DataType>} sortBy
 */

/**
 * @template DataType
 * @typedef {Object} SimpleTableProps
 * @prop {DataType[]} data - Data to display
 * @prop {ColumnType<DataType>[]} columns - Column definitions
 * @prop {import('./LocalizedPagination').PageInfo} [pageInfo] - Use this when the paging is handled outside this component (eg by API)
 * @prop {OnRowClickCallback<DataType>} [onRowClick] - Called when a row is clicked
 * @prop {boolean} [isLoading=false] - True if table is loading or otherwise busy
 * @prop {ActionType<DataType>[]} [actions] - Actions for each row
 * @prop {OnSearchCallback} [onSearch] - Called when the search text changes
 * @prop {SortByType<DataType>} [sortBy] - Defines the current sort
 * @prop {SimpleTableOnSortCallback<DataType>} [onSort] - Called when a column is sorted
 * @prop {DatafilterCallback<DataType>} [dataFilter] - Use this when the filtering is handled outside this component (eg by API)
 * @prop {boolean} [hasError=false] - True if table has an error
 * @prop {string} [errorText=T.common.error.generic] - Error text to display when table has an error
 * @prop {RowSelection} [rowSelection] - Defines the row selection mode.
 * @prop {array} [selectedRows] - Current selected rows
 * @prop {OnSelectedRowsCallback<DataType>} [onSelectedRows] - Called when selected rows changes
 * @prop {boolean} [compact=false] - True if table should be compact (smaller)
 * @prop {GlobalAction[]} [globalActions] - Global actions to display in the toolbar
 * @prop {function (DataType): string} [rowIteratee=_.identity] - Function to use to get a unique identifier for a row. Used for selection and dragging.
 * @prop {string} [emptyListText=T.common.list.empty] - Text to display when table is empty
 * @prop {string} [tableClassName=''] - Class name for the table
 * @prop {string} [className] - Class name for the table container
 * @prop {boolean} [isDraggable=false] - True if table rows should be draggable. When defined the rowIteratee must be set as well.
 * @prop {OrderChangedCallback<DataType>} [onDataOrderChanged] - Called when the data order changes (rows are dragged)
 * @prop {boolean} [usePaging=false] - True if paging should be used
 * @prop {number} [pageSize=20] - Number of items per page when usePaging is True
 * @prop {PageChangedCallback<DataType>} [onPageChanged] - Called when the page changes, also called the first time the table is rendered to return the initial page data
*/

/**
 * @description A simple table component that supports paging, sorting, filtering and selection
 * @template {Record<string, any>} DataType
 * @param {SimpleTableProps<DataType>} props
 * @example <caption>Example usage of SimpleTable</caption>
 * const columns = [
 *   {
 *     path: 'name',
 *     label: 'Name',
 *     searchItem: true,
 *     isSortable: true
 *   },
 *   {
 *     path: 'description',
 *     label: 'Description',
 *     searchItem: true,
 *     isSortable: true
 *   },
 *   {
 *     path: 'type',
 *     label: 'Type',
 *     searchItem: true,
 *    isSortable: true
 *   }
 * ];
 *
 * const data = [
 *   {
 *     name: 'Item 1',
 *     description: 'Description 1',
 *     type: 'Type 1'
 *   },
 *   {
 *     name: 'Item 2',
 *     description: 'Description 2',
 *     type: 'Type 2'
 *   }
 * ];
 *
 * const actions = [
 *   {
 *     title: 'Edit',
 *     onClick: () => { console.log('Edit'); }
 *   }
 * ];
 *
 * const globalActions = [
 *  {
 *    label: 'Add',
 *    onClick: () => { console.log('Add'); }
 *   }
 * ];
 *
 * return (
 *   <SimpleTable
 *     columns={columns}
 *     data={data}
 *     actions={actions}
 *     globalActions={globalActions}
 *   />
 * );
 *
 * @returns {JSX.Element}
 */
const SimpleTable = (
  {
    data: _data,
    columns,
    pageInfo: _pageInfo,
    onRowClick,
    isLoading,
    actions: _actions,
    onSearch,
    sortBy: _sortBy, onSort: _onSort,
    dataFilter,
    hasError = false, errorText = Tl.common.error.generic,
    rowSelection = RowSelection.NONE,
    selectedRows,
    onSelectedRows,
    compact = false,
    globalActions,
    rowIteratee = _.identity,
    emptyListText = Tl.common.list.empty,
    tableClassName = '',
    className,
    isDraggable = false,
    onDataOrderChanged,
    usePaging = false,
    pageSize = 20,
    onPageChanged
  }) => {
  const [sortBy, setSortBy] = useState(_sortBy);
  const [nonDeferredSearchText, setSearchText] = useState('');
  const searchText = useDeferredValue(nonDeferredSearchText);

  const T = useTranslation();

  const hasSearchItem = _.some(columns, 'searchItem');

  const filteredData = useMemo(() => {
    if (dataFilter) {
      return dataFilter(_data, columns, searchText, sortBy);
    }

    const searchItemPaths = _.map(_.filter(columns, 'searchItem'), 'path');

    const filtered = _.isEmpty(searchItemPaths) ? _data : search(_data, searchText, ...searchItemPaths);
    return sortBy?.path != null && sortBy?.direction != null ?
      _.orderBy(filtered, item => {
        const itemValue = _.get(item, sortBy.path);
        if (_.isString(itemValue)) {
          return itemValue?.toLowerCase();
        }
        return itemValue;
      }, [/** @type {'desc'|'asc'} */(sortBy.direction)])
      : filtered;
  }, [dataFilter, columns, _data, searchText, sortBy]);

  const pageInfo = useSimpleTablePager({ data: filteredData, pageSize: pageSize });

  const data = useMemo(() => {
    return usePaging ?
      // page data
      _.slice(filteredData, (pageInfo.page - 1) * pageInfo.perPage, pageInfo.page * pageInfo.perPage) :
      // all data
      filteredData;
  }, [filteredData, pageInfo, usePaging]);

  /**
   * @type {React.MutableRefObject<import('./LocalizedPagination').PageInfo>}
   */
  const oldPage = useRef(null);

  // Call onPageChanged when page changes and when the table is rendered the first time
  // So the parent knows the page data and page info
  useEffect(() => {
    if (usePaging && onPageChanged &&
       (oldPage.current?.page !== pageInfo.page ||
       oldPage.current?.perPage !== pageInfo.perPage ||
       oldPage.current?.itemCount !== pageInfo.itemCount)) {
      onPageChanged(data, pageInfo.page, pageInfo.perPage);
      oldPage.current = { ...pageInfo };
    }
  }, [pageInfo, onPageChanged, usePaging, data]);


  const onSort = useCallback((column, direction) => {
    _onSort ? _onSort({ path: column.path, direction }) : setSortBy({ path: column.path, direction });
  }, [_onSort]);

  const actions = useMemo(() => _.map(_actions, action => ({
    ...action,
    title: T.localize(action.title)
  })), [T, _actions]);

  const _globalActions = useMemo(() => _.map(globalActions, action => ({
    ...action,
    label: T.localize(action.label) ?? T.common.add,
    variant: action.variant ?? ButtonVariant.primary,
    isDisabled: action.isDisabled ?? false
  })), [T, globalActions]);

  const onSelectAll = useCallback((/** @type {boolean}*/select) => {
    if (select) {
      if (_.isEmpty(selectedRows)) {
        onSelectedRows(data);
      } else {
        // append all data that was not in current selection
        // e.g. do not replace the current selected data which can be outside the current 'data' collection (think paging)
        const notSelected = _.reject(data, isItemInCollection(selectedRows, rowIteratee));
        onSelectedRows([...selectedRows, ...notSelected]);
      }
    } else {
      const rowsNotInData = _.reject(selectedRows, isItemInCollection(data, rowIteratee));
      onSelectedRows(rowsNotInData);
    }
  }, [data, selectedRows, rowIteratee, onSelectedRows]);

  const isAllSelected = useMemo(() => _.every(data, isItemInCollection(selectedRows, rowIteratee)),
    [data, selectedRows, rowIteratee]);

  const clearSearch = useCallback(() => setSearchText(''), []);

  const [isDragging, setDragging, unsetDragging] = useBoolean(false);
  const bodyRef = useRef(null);
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [draggingToItemIndex, setDraggingToItemIndex] = React.useState(null);
  const [itemOrder, setItemOrder] = React.useState([]);
  const [tempItemOrder, setTempItemOrder] = React.useState([]);

  useEffect(() => {
    setItemOrder(_.map(data, rowIteratee));
  }, [data, rowIteratee]);

  const onDragOver = evt => {
    evt.preventDefault();
    const curListItem = evt.target.closest('tr');

    if (!curListItem || !bodyRef.current.contains(curListItem) || curListItem.id === draggedItemId) {
      return null;
    }

    const dragId = curListItem.id;
    const newDraggingToItemIndex = Array.from(bodyRef.current.children).findIndex(item => item.id === dragId);
    if (newDraggingToItemIndex !== draggingToItemIndex) {
      const newTempItemOrder = moveItem([...itemOrder], draggedItemId, newDraggingToItemIndex);
      move(newTempItemOrder, bodyRef.current);
      setDraggingToItemIndex(newDraggingToItemIndex);
      setTempItemOrder(newTempItemOrder);
    }

  };

  const onDrop = useCallback(() => {
    setItemOrder(tempItemOrder);
    // TODO: In large sets this needs to be optimized.
    // It works for now
    const newDataOrder = _.sortBy(data, item => tempItemOrder.indexOf(rowIteratee(item)));
    onDataOrderChanged?.(newDataOrder);
  }, [data, onDataOrderChanged, rowIteratee, tempItemOrder]);

  const onDragCancel = useCallback(() => {
    Array.from(bodyRef.current.children).forEach(el => {
      el.classList.remove(styles.modifiers.ghostRow);
      el.setAttribute('aria-pressed', 'false');
    });
    setDraggedItemId(null);
    setDraggingToItemIndex(null);
    unsetDragging();
  }, [unsetDragging]);

  const onDragLeave = useCallback(event => {
    if (!isValidDrop(event, bodyRef.current)) {
      move(itemOrder, bodyRef.current);
      setDraggingToItemIndex(null);
    }
  }, [itemOrder]);

  const dataState = useCallback((state) => {
    switch (state) {
      case State.EMPTY:
        return (
          <Tr>
            <Td colSpan={8}>
              <Bullseye>
                <EmptyState variant={EmptyStateVariant.sm}>
                  { !_.isEmpty(searchText) && <EmptyStateIcon icon={Icons.Search}/> }
                  <EmptyStateHeader titleText={<>{ T.localize(emptyListText) }</>} headingLevel="h2" /><EmptyStateFooter>
                    { !_.isEmpty(searchText) && (
                      <>
                        <EmptyStateBody>{ T.common.list.clearFilters }</EmptyStateBody>
                        <Button variant={ButtonVariant.link} onClick={clearSearch}>{ T.common.list.clearFiltersAction }</Button>
                      </>
                    ) }
                  </EmptyStateFooter></EmptyState>
              </Bullseye>
            </Td>
          </Tr>
        );
      case State.LOADING:
        return (
          <Tr>
            <Td colSpan={8} rowSpan={4}>
              <Bullseye>
                <Spinner size="lg" />
              </Bullseye>
            </Td>
          </Tr>
        );
      case State.DATA:
        return (
          <Rows
            data={data}
            columns={columns}
            onRowClick={onRowClick}
            actions={actions}
            selectedRows={selectedRows}
            rowSelection={rowSelection}
            onSelectedRows={onSelectedRows}
            rowIteratee={rowIteratee}
            onDragStart={setDragging}
            onDragEnd={unsetDragging}
            onDragCancel={onDragCancel}
            onDrop={onDrop}
            bodyRef={bodyRef}
            isDraggable={isDraggable}
            setDraggedItemId={setDraggedItemId}
          />
        );
      default:
        return (<></>);
    }
  }, [T, actions, clearSearch, columns, data, emptyListText, isDraggable, onDragCancel, onDrop, onRowClick, onSelectedRows, rowIteratee, rowSelection, searchText, selectedRows, setDragging, unsetDragging]);


  let state = State.DATA;
  if (isLoading) {
    state = State.LOADING;
  } else if (_.isEmpty(data)) {
    state = State.EMPTY;
  }

  if (hasError) {
    return (<Alert variant={AlertVariant.danger} isInline title={T.localize(errorText)} />);
  }
  const realPageInfo = usePaging ? pageInfo : _pageInfo;

  const onSearchItems = (searchText) => {
    if (hasSearchItem) {
      if (onSearch) {
        onSearch(searchText);
      }
      setSearchText(searchText);
    }
    return null;
  };

  return (
    <Stack className={classNames(className, 'main-container')}>
      <StackItem>
        { (hasSearchItem || !_.isEmpty(_globalActions) || pageInfo) &&
          (<Toolbar id="toolbar">
            <ToolbarContent>
              <DefaultToolbarItems
                onSearch={onSearchItems}
                searchText={nonDeferredSearchText}
                actions={_globalActions}
                pageInfo={realPageInfo} />
            </ToolbarContent>
          </Toolbar>)
        }
      </StackItem>
      <StackItem isFilled className={'simple-table-scroll'}>
        <InnerScrollContainer>
          <Table
            borders
            variant={compact ? TableVariant.compact : undefined}
            className={classNames(tableClassName, isDragging && styles.modifiers.dragOver)}
            isStickyHeader>
            <Thead>
              <Tr>
                <Columns
                  columns={columns}
                  onSort={onSort}
                  sortBy={sortBy}
                  rowSelection={rowSelection}
                  onSelectAll={onSelectAll}
                  isAllSelected={isAllSelected}
                  actions={actions}
                  isDraggable={isDraggable}
                />
              </Tr>
            </Thead>
            <Tbody ref={bodyRef} onDragOver={onDragOver} onDrop={onDragOver} onDragLeave={onDragLeave}>
              { dataState(state) }
            </Tbody>
          </Table>
        </InnerScrollContainer>
        { realPageInfo && <LocalizedPagination pageInfo={realPageInfo} variant={PaginationVariant.bottom}/> }
      </StackItem>
    </Stack>
  );
};

SimpleTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.array])),
  columns: PropTypes.arrayOf(ColumnPropType),
  pageInfo: PageInfoPropType,
  onRowClick: PropTypes.func,
  isLoading: PropTypes.bool,
  globalActions: PropTypes.arrayOf(GlobalActionPropType),
  actions: PropTypes.arrayOf(ActionPropType),
  onSearch: PropTypes.func,
  sortBy: SortByPropType,
  onSort: PropTypes.func,
  dataFilter: PropTypes.func,
  hasError: PropTypes.bool,
  errorText: PropTypes.string,
  rowSelection: PropTypes.oneOf(_.values(RowSelection)),
  selectedRows: PropTypes.array,
  onSelectedRows: PropTypes.func,
  compact: PropTypes.bool,
  rowIteratee: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  emptyListText: PropTypes.string,
  tableClassName: PropTypes.string,
  className: PropTypes.string,
  isDraggable: PropTypes.bool,
  onDataOrderChanged: PropTypes.func,
  usePaging: PropTypes.bool,
  pageSize: PropTypes.number
};

export default React.memo(SimpleTable);
